import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { ErrorState } from '../../../components/ui/ErrorState';
import { TextField } from '../components/TextField';
import { updateProfile } from '../../../features/user/userThunks';
import { isValidEmail } from '../../../features/user/userConstants';

const EditProfileDialog = ({ onClose, user }) => {
  const dispatch = useDispatch();
  const { isSaving } = useSelector((state) => state.user);

  // Read once: the account page fetches the user on mount, and a late answer
  // used to put back the stored name and email over what was typed.
  const [form, setForm] = useState(() => ({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
  }));
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setFieldError('');
    setError(null);
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const email = form.email.trim();
    if (!isValidEmail(email)) {
      setFieldError('Escribe un correo válido');
      return;
    }

    try {
      await dispatch(
        updateProfile({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email,
        })
      ).unwrap();
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudieron guardar los cambios');
    }
  };

  const emailChanged = form.email.trim() !== (user?.email || '');

  return (
    <Modal isOpen onClose={handleClose} title="Editar Perfil" busy={isSaving}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && <ErrorState variant="inline" title="No se pudo guardar" description={error} />}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            id="account-first-name"
            label="Nombre"
            value={form.first_name}
            onChange={handleChange('first_name')}
            autoComplete="given-name"
          />
          <TextField
            id="account-last-name"
            label="Apellido"
            value={form.last_name}
            onChange={handleChange('last_name')}
            autoComplete="family-name"
          />
        </div>

        <TextField
          id="account-email"
          label="Correo"
          type="email"
          value={form.email}
          onChange={handleChange('email')}
          error={fieldError}
          hint="Es también tu usuario para iniciar sesión."
          autoComplete="email"
        />

        {emailChanged && (
          <p className="text-sm text-warning">
            Vas a cambiar el correo con el que inicias sesión.
          </p>
        )}

        <div className="flex items-center justify-end gap-3 mt-2">
          <Button variant="subtle" pill type="button" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            pill
            type="submit"
            loading={isSaving}
            loadingLabel="Guardando..."
          >
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Name, surname and email in one dialog.
 *
 * One dialog rather than the three inline editable fields the legacy uses: the
 * mockup already draws a single "Editar Perfil" button, and three separate save
 * buttons on a read-only-looking card is the kind of thing people click by
 * accident.
 *
 * Behind it there are still two endpoints, and the thunk sends the email first
 * on purpose — see `updateProfile`.
 *
 * Mounted only while open: each opening starts from the user as it is then, and
 * nothing that arrives while it is open overwrites the form.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.user - The `get_user/` payload.
 */
export const EditProfileModal = (props) => (props.isOpen ? <EditProfileDialog {...props} /> : null);
